import React, { useState, useEffect, useCallback } from 'react';
import { liabilitiesApi, entitiesApi } from '../api/client';
import { Plus, Pencil, Trash2, X, FileText, AlertCircle, Search, ChevronRight } from 'lucide-react';

const LIABILITY_TYPES = [
  { value: 'mortgage', label: 'Mortgage' },
  { value: 'margin_loan', label: 'Margin Loan' },
  { value: 'credit_line', label: 'Credit Line' },
  { value: 'term_loan', label: 'Term Loan' },
  { value: 'pledge_facility', label: 'Pledge Facility' },
  { value: 'private_note', label: 'Private Note' },
  { value: 'other', label: 'Other' },
];

const AMORT_TYPES = [
  { value: 'amortizing', label: 'Amortizing' },
  { value: 'io', label: 'Interest Only' },
  { value: 'balloon', label: 'Balloon' },
];

const PAYMENT_FREQUENCIES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semi_annual', label: 'Semi-Annual' },
  { value: 'annual', label: 'Annual' },
];

function typeLabel(val: string): string { return LIABILITY_TYPES.find(t => t.value === val)?.label || val; }
function amortLabel(val: string | undefined): string { return AMORT_TYPES.find(t => t.value === val)?.label || '—'; }

function fmtCurrency(val: number | string | null | undefined): string {
  if (val == null) return '—';
  const n = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(n)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
}

function fmtCompact(val: number | string | null | undefined): string {
  if (val == null) return '$0';
  const n = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(n)) return '$0';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function fmtRate(val: number | string | null | undefined): string {
  if (val == null) return '—';
  const n = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(n)) return '—';
  return `${(n * 100).toFixed(2)}%`;
}

function fmtDate(val: string | null | undefined): string {
  if (!val) return '—';
  try { return new Date(val).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }); }
  catch { return val; }
}

interface Liability {
  id: string; name: string; liability_type: string; entity_id?: string; entity_name?: string;
  outstanding_balance: number | string; credit_limit?: number | string;
  interest_rate?: number | string; is_variable_rate?: boolean; maturity_date?: string;
  amortization_type?: string; payment_frequency?: string; annual_debt_service?: number | string;
  prepayment_penalty_pct?: number | string; prepayment_penalty_expires?: string;
  notes?: string;
  amortization_term_months?: number;
  balloon_amount?: number | string;
  balloon_amount_override?: number | string;
  refi_expected?: boolean;
  refi_rate?: number | string;
  refi_term_months?: number;
  refi_am_term_months?: number;
}

interface EntityOption { id: string; name: string; }

interface FormData {
  name: string; liabilityType: string; entityId: string;
  outstandingBalance: string; creditLimit: string; interestRate: string;
  isVariableRate: boolean; maturityDate: string; notes: string;
  amortizationType: string; paymentFrequency: string; annualDebtService: string;
  prepaymentPenaltyPct: string; prepaymentPenaltyExpires: string;
  amortizationTermMonths: string; balloonAmountOverride: string;
  refiExpected: boolean; refiRate: string; refiTermMonths: string; refiAmTermMonths: string;
}

const emptyForm: FormData = {
  name: '', liabilityType: 'mortgage', entityId: '',
  outstandingBalance: '', creditLimit: '', interestRate: '',
  isVariableRate: false, maturityDate: '', notes: '',
  amortizationType: '', paymentFrequency: 'monthly', annualDebtService: '',
  prepaymentPenaltyPct: '', prepaymentPenaltyExpires: '',
  amortizationTermMonths: '', balloonAmountOverride: '',
  refiExpected: false, refiRate: '', refiTermMonths: '', refiAmTermMonths: '',
};

function TypeBadge({ type }: { type: string }) {
  const secured = ['mortgage', 'pledge_facility'];
  const credit = ['credit_line', 'margin_loan'];
  let classes = 'badge-navy';
  if (secured.includes(type)) classes = 'badge-burgundy';
  else if (credit.includes(type)) classes = 'badge-gold';
  return <span className={classes}>{typeLabel(type)}</span>;
}

function MaturityBadge({ date }: { date?: string }) {
  if (!date) return <span className="text-xs text-navy-300">—</span>;
  const d = new Date(date);
  const now = new Date();
  const monthsOut = (d.getFullYear() - now.getFullYear()) * 12 + d.getMonth() - now.getMonth();
  if (monthsOut < 0) return <span className="badge-burgundy">Matured</span>;
  if (monthsOut < 12) return <span className="badge-burgundy">{fmtDate(date)}</span>;
  if (monthsOut < 24) return <span className="badge-gold">{fmtDate(date)}</span>;
  return <span className="text-sm text-navy-500 font-sans">{fmtDate(date)}</span>;
}

export default function LiabilitiesPage() {
  const [liabilities, setLiabilities] = useState<Liability[]>([]);
  const [entities, setEntities] = useState<EntityOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterEntity, setFilterEntity] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchLiabilities = useCallback(async () => {
    try { setLoading(true); const res = await liabilitiesApi.list({ limit: 100 }); setLiabilities(res.data.data || []); setError(''); }
    catch (err: any) { setError(err.response?.data?.error || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);

  const fetchEntities = useCallback(async () => {
    try { const res = await entitiesApi.list({ limit: 100 }); setEntities((res.data.data || []).map((e: any) => ({ id: e.id, name: e.name }))); } catch (_) {}
  }, []);

  useEffect(() => { fetchLiabilities(); fetchEntities(); }, [fetchLiabilities, fetchEntities]);

  const totalBalance = liabilities.reduce((s, l) => s + (parseFloat(l.outstanding_balance as string) || 0), 0);
  const totalLimit = liabilities.reduce((s, l) => s + (parseFloat(l.credit_limit as string) || 0), 0);
  const totalDebtService = liabilities.reduce((s, l) => s + (parseFloat(l.annual_debt_service as string) || 0), 0);
  const variableCount = liabilities.filter(l => l.is_variable_rate).length;
  const filtered = liabilities
    .filter(l => !filterEntity || l.entity_id === filterEntity)
    .filter(l => !search || l.name.toLowerCase().includes(search.toLowerCase()));

  function openCreate() { setEditingId(null); setForm(emptyForm); setFormError(''); setShowModal(true); }

  function openEdit(l: Liability) {
    setEditingId(l.id);
    setForm({
      name: l.name, liabilityType: l.liability_type, entityId: l.entity_id || '',
      outstandingBalance: String(l.outstanding_balance || ''),
      creditLimit: String(l.credit_limit || ''),
      interestRate: String((parseFloat(l.interest_rate as string) || 0) * 100),
      isVariableRate: l.is_variable_rate || false,
      maturityDate: l.maturity_date ? l.maturity_date.substring(0, 10) : '',
      notes: l.notes || '',
      amortizationType: l.amortization_type || '',
      paymentFrequency: l.payment_frequency || 'monthly',
      annualDebtService: String(l.annual_debt_service || ''),
      prepaymentPenaltyPct: String(l.prepayment_penalty_pct || ''),
      prepaymentPenaltyExpires: l.prepayment_penalty_expires ? l.prepayment_penalty_expires.substring(0, 10) : '',
      amortizationTermMonths: l.amortization_term_months ? String(l.amortization_term_months) : '',
      balloonAmountOverride: l.balloon_amount_override ? String(l.balloon_amount_override) : '',
      refiExpected: l.refi_expected || false,
      refiRate: l.refi_rate ? String((parseFloat(l.refi_rate as string) || 0) * 100) : '',
      refiTermMonths: l.refi_term_months ? String(l.refi_term_months) : '',
      refiAmTermMonths: l.refi_am_term_months ? String(l.refi_am_term_months) : '',
    });
    setFormError(''); setShowModal(true);
  }

  async function handleSave() {
    if (!form.name.trim()) { setFormError('Name is required'); return; }
    if (!form.outstandingBalance || parseFloat(form.outstandingBalance) < 0) { setFormError('Balance is required'); return; }
    try {
      setSaving(true); setFormError('');
      const payload: any = {
        name: form.name.trim(), liabilityType: form.liabilityType,
        entityId: form.entityId || undefined,
        outstandingBalance: parseFloat(form.outstandingBalance),
        creditLimit: form.creditLimit ? parseFloat(form.creditLimit) : undefined,
        interestRate: form.interestRate ? parseFloat(form.interestRate) / 100 : undefined,
        isVariableRate: form.isVariableRate,
        maturityDate: form.maturityDate || undefined,
        notes: form.notes || undefined,
        amortizationType: form.amortizationType || undefined,
        paymentFrequency: form.paymentFrequency || undefined,
        annualDebtService: form.annualDebtService ? parseFloat(form.annualDebtService) : undefined,
        prepaymentPenaltyPct: form.prepaymentPenaltyPct ? parseFloat(form.prepaymentPenaltyPct) : undefined,
        prepaymentPenaltyExpires: form.prepaymentPenaltyExpires || undefined,
        amortizationTermMonths: form.amortizationTermMonths ? parseInt(form.amortizationTermMonths) : undefined,
        balloonAmountOverride: form.balloonAmountOverride ? parseFloat(form.balloonAmountOverride) : undefined,
        refiExpected: form.refiExpected,
        refiRate: form.refiRate ? parseFloat(form.refiRate) / 100 : undefined,
        refiTermMonths: form.refiTermMonths ? parseInt(form.refiTermMonths) : undefined,
        refiAmTermMonths: form.refiAmTermMonths ? parseInt(form.refiAmTermMonths) : undefined,
      };
      if (editingId) await liabilitiesApi.update(editingId, payload);
      else await liabilitiesApi.create(payload);
      setShowModal(false); fetchLiabilities();
    } catch (err: any) { setFormError(err.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!deleteId) return;
    try { setDeleting(true); await liabilitiesApi.delete(deleteId); setDeleteId(null); fetchLiabilities(); }
    catch (err: any) { setError(err.response?.data?.error || 'Failed to delete'); setDeleteId(null); }
    finally { setDeleting(false); }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-navy-900">Debt Schedule</h1>
          <p className="text-sm text-navy-400 font-sans mt-1">Obligations, credit facilities, and debt instruments across all entities</p>
        </div>
        <button onClick={openCreate} className="btn-primary"><Plus size={16} className="mr-2" />Add Liability</button>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="card"><p className="stat-label mb-1">Total Outstanding</p><p className="font-display text-xl text-burgundy-500">{fmtCompact(totalBalance)}</p><p className="text-xs text-navy-400 font-sans mt-1">{liabilities.length} instruments</p></div>
        <div className="card"><p className="stat-label mb-1">Total Capacity</p><p className="font-display text-xl text-navy-900">{fmtCompact(totalLimit)}</p><p className="text-xs text-navy-400 font-sans mt-1">{totalLimit > totalBalance ? `${fmtCompact(totalLimit - totalBalance)} available` : '—'}</p></div>
        <div className="card"><p className="stat-label mb-1">Annual Debt Service</p><p className="font-display text-xl text-gold-500">{fmtCompact(totalDebtService)}</p><p className="text-xs text-navy-400 font-sans mt-1">total across instruments</p></div>
        <div className="card"><p className="stat-label mb-1">Variable Rate</p><p className="font-display text-xl text-navy-900">{variableCount}</p><p className="text-xs text-navy-400 font-sans mt-1">{liabilities.length > 0 ? `${((variableCount / liabilities.length) * 100).toFixed(0)}% of instruments` : '—'}</p></div>
      </div>

      <div className="card mb-6">
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-300" />
            <input type="text" placeholder="Search liabilities..." value={search} onChange={(e) => setSearch(e.target.value)} className="input pl-9" />
          </div>
          <select value={filterEntity} onChange={(e) => setFilterEntity(e.target.value)} className="input w-auto">
            <option value="">All Entities</option>
            {entities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          {filterEntity && (
            <button onClick={() => setFilterEntity('')} className="text-xs text-navy-400 hover:text-burgundy-500 font-sans underline">Clear</button>
          )}
          <div className="ml-auto text-sm text-navy-400 font-sans">{filtered.length} {filtered.length === 1 ? 'instrument' : 'instruments'}</div>
        </div>
      </div>

      {error && <div className="flex items-center gap-2 p-4 mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm"><AlertCircle size={16} />{error}</div>}

      <div className="card p-0 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><p className="text-sm text-navy-400 font-sans animate-pulse">Loading...</p></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center"><FileText size={32} className="mx-auto mb-3 text-navy-200" /><p className="text-sm text-navy-500 font-sans font-medium">No liabilities yet</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ivory-300 bg-ivory-200">
                  <th className="table-header w-10 px-2 py-3"></th>
                  <th className="table-header text-left px-6 py-3">Instrument</th>
                  <th className="table-header text-left px-4 py-3">Type</th>
                  <th className="table-header text-right px-4 py-3">Balance</th>
                  <th className="table-header text-right px-4 py-3">Rate</th>
                  <th className="table-header text-center px-4 py-3">Amort</th>
                  <th className="table-header text-right px-4 py-3">Debt Svc</th>
                  <th className="table-header text-left px-4 py-3">Maturity</th>
                  <th className="table-header text-right px-4 py-3">Balloon</th>
                  <th className="table-header text-right px-6 py-3 w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ivory-200">
                {filtered.map((l) => (
                  <React.Fragment key={l.id}>
                  <tr className="hover:bg-ivory-100 transition-colors">
                    <td className="px-2 py-3.5 text-center">
                      <button onClick={() => setExpandedId(expandedId === l.id ? null : l.id)} className="p-1 rounded hover:bg-ivory-200 text-navy-400 hover:text-navy-700 transition-all">
                        <ChevronRight size={14} className={`transition-transform duration-200 ${expandedId === l.id ? 'rotate-90' : ''}`} />
                      </button>
                    </td>
                    <td className="px-6 py-3.5">
                      <span className="font-sans text-sm font-semibold text-navy-800">{l.name}</span>
                      {l.entity_name && <p className="text-xs text-navy-400 mt-0.5">{l.entity_name}</p>}
                    </td>
                    <td className="px-4 py-3.5"><TypeBadge type={l.liability_type} /></td>
                    <td className="px-4 py-3.5 text-right font-mono text-sm text-burgundy-500 font-medium">{fmtCurrency(l.outstanding_balance)}</td>
                    <td className="px-4 py-3.5 text-right font-mono text-sm text-navy-600">
                      {fmtRate(l.interest_rate)}
                      {l.is_variable_rate && <span className="ml-1 text-xs text-gold-500">V</span>}
                    </td>
                    <td className="px-4 py-3.5 text-center text-xs text-navy-500 font-sans">{amortLabel(l.amortization_type)}</td>
                    <td className="px-4 py-3.5 text-right font-mono text-sm text-navy-600">{fmtCurrency(l.annual_debt_service)}</td>
                    <td className="px-4 py-3.5"><MaturityBadge date={l.maturity_date} /></td>
                    <td className="px-4 py-3.5 text-right font-mono text-sm">
                      {parseFloat(l.balloon_amount as string) > 0 ? (
                        <span className="text-burgundy-500 font-medium">{fmtCurrency(l.balloon_amount)}</span>
                      ) : l.amortization_type === 'io' ? (
                        <span className="text-burgundy-500 font-medium">{fmtCurrency(l.outstanding_balance)}</span>
                      ) : (
                        <span className="text-hunter-500 text-xs">No balloon</span>
                      )}
                    </td>
                    <td className="px-6 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(l)} className="p-1.5 rounded hover:bg-ivory-200 text-navy-400 hover:text-navy-700 transition-colors" title="Edit"><Pencil size={14} /></button>
                        <button onClick={() => setDeleteId(l.id)} className="p-1.5 rounded hover:bg-red-50 text-navy-400 hover:text-red-600 transition-colors" title="Delete"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                  {expandedId === l.id && (
                    <tr className="bg-ivory-100 border-b border-ivory-200">
                      <td colSpan={10} className="px-6 py-4">
                        <div className="grid grid-cols-3 gap-6">
                          <div>
                            <p className="text-xs font-sans font-semibold text-navy-500 uppercase tracking-wider mb-2">Instrument Details</p>
                            <div className="space-y-1.5">
                              <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Type</span><span className="text-xs text-navy-700 font-sans font-medium">{amortLabel(l.amortization_type)}{l.is_variable_rate ? ' (Variable)' : ' (Fixed)'}</span></div>
                              <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Interest Rate</span><span className="text-xs text-navy-700 font-mono">{fmtRate(l.interest_rate)}</span></div>
                              <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Outstanding</span><span className="text-xs text-burgundy-500 font-mono font-medium">{fmtCurrency(l.outstanding_balance)}</span></div>
                              {l.credit_limit && parseFloat(l.credit_limit as string) > 0 && (
                                <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Credit Limit</span><span className="text-xs text-navy-700 font-mono">{fmtCurrency(l.credit_limit)}</span></div>
                              )}
                              <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Annual Debt Svc</span><span className="text-xs text-navy-700 font-mono">{fmtCurrency(l.annual_debt_service)}</span></div>
                            </div>
                          </div>
                          <div>
                            <p className="text-xs font-sans font-semibold text-navy-500 uppercase tracking-wider mb-2">Terms & Entity</p>
                            <div className="space-y-1.5">
                              <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Maturity</span><span className="text-xs text-navy-700 font-sans">{fmtDate(l.maturity_date)}</span></div>
                              <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Entity</span><span className="text-xs text-navy-700 font-sans">{l.entity_name || 'Personal'}</span></div>
                              <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Payment Freq</span><span className="text-xs text-navy-700 font-sans capitalize">{l.payment_frequency || '—'}</span></div>
                              {l.prepayment_penalty_pct && parseFloat(l.prepayment_penalty_pct as string) > 0 && (
                                <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Prepay Penalty</span><span className="text-xs text-navy-700 font-sans">{l.prepayment_penalty_pct}%{l.prepayment_penalty_expires ? ` until ${fmtDate(l.prepayment_penalty_expires)}` : ''}</span></div>
                              )}
                            </div>
                          </div>
                          <div>
                            <p className="text-xs font-sans font-semibold text-navy-500 uppercase tracking-wider mb-2">Balloon & Refi</p>
                            <div className="space-y-1.5">
                              {parseFloat(l.balloon_amount as string) > 0 ? (
                                <>
                                  <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Balloon Amount</span><span className="text-xs text-burgundy-500 font-mono font-semibold">{fmtCurrency(l.balloon_amount)}</span></div>
                                  {l.amortization_term_months && <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Am Schedule</span><span className="text-xs text-navy-700 font-sans">{l.amortization_term_months} months ({Math.round(l.amortization_term_months / 12)}yr)</span></div>}
                                  {l.refi_expected && (
                                    <>
                                      <div className="mt-2 pt-2 border-t border-ivory-300"></div>
                                      <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Refi Rate</span><span className="text-xs text-navy-700 font-mono">{fmtRate(l.refi_rate)}</span></div>
                                      {l.refi_term_months && <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Refi Term</span><span className="text-xs text-navy-700 font-sans">{l.refi_term_months} mo ({Math.round(l.refi_term_months / 12)}yr)</span></div>}
                                      {l.refi_am_term_months && <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Refi Am Sched</span><span className="text-xs text-navy-700 font-sans">{l.refi_am_term_months} mo ({Math.round(l.refi_am_term_months / 12)}yr)</span></div>}
                                    </>
                                  )}
                                </>
                              ) : l.amortization_type === 'io' || l.amortization_type === 'interest_only' ? (
                                <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Balloon at Maturity</span><span className="text-xs text-burgundy-500 font-mono font-semibold">{fmtCurrency(l.outstanding_balance)}</span></div>
                              ) : (
                                <span className="text-xs text-hunter-500 font-sans font-medium">Fully amortizing — no balloon</span>
                              )}
                            </div>
                          </div>
                        </div>
                        {l.notes && (
                          <div className="mt-3 pt-3 border-t border-ivory-300">
                            <p className="text-xs text-navy-400 font-sans italic">{l.notes}</p>
                          </div>
                        )}
                        <div className="mt-3 flex justify-end">
                          <button onClick={() => openEdit(l)} className="text-xs font-sans font-medium text-navy-500 hover:text-navy-700 underline">Edit full details</button>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Create/Edit Modal ──────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-xl border border-ivory-300 w-full max-w-xl mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-ivory-300">
              <h3 className="font-display text-lg text-navy-900">{editingId ? 'Edit Liability' : 'Add Liability'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded hover:bg-ivory-200 text-navy-400"><X size={18} /></button>
            </div>
            <div className="px-6 py-5 space-y-4 max-h-[65vh] overflow-y-auto">
              {formError && <div className="flex items-center gap-2 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm"><AlertCircle size={14} />{formError}</div>}

              <div>
                <label className="input-label">Instrument Name *</label>
                <input type="text" className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., First Lien — Office Complex" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="input-label">Type *</label>
                  <select className="input" value={form.liabilityType} onChange={(e) => setForm({ ...form, liabilityType: e.target.value })}>
                    {LIABILITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="input-label">Entity</label>
                  <select className="input" value={form.entityId} onChange={(e) => setForm({ ...form, entityId: e.target.value })}>
                    <option value="">— Personal —</option>
                    {entities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="input-label">Outstanding Balance ($) *</label>
                  <input type="number" className="input" value={form.outstandingBalance} onChange={(e) => setForm({ ...form, outstandingBalance: e.target.value })} placeholder="0" min="0" />
                </div>
                <div>
                  <label className="input-label">Credit Limit ($)</label>
                  <input type="number" className="input" value={form.creditLimit} onChange={(e) => setForm({ ...form, creditLimit: e.target.value })} placeholder="0" min="0" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="input-label">Interest Rate (%)</label>
                  <input type="number" className="input" value={form.interestRate} onChange={(e) => setForm({ ...form, interestRate: e.target.value })} placeholder="5.75" step="0.01" />
                </div>
                <div>
                  <label className="input-label">Maturity Date</label>
                  <input type="date" className="input" value={form.maturityDate} onChange={(e) => setForm({ ...form, maturityDate: e.target.value })} />
                </div>
              </div>
              <div className="flex items-center gap-3 py-1">
                <input type="checkbox" id="isVariableRate" checked={form.isVariableRate} onChange={(e) => setForm({ ...form, isVariableRate: e.target.checked })} className="w-4 h-4 rounded border-ivory-400 text-navy-600 focus:ring-navy-300" />
                <label htmlFor="isVariableRate" className="text-sm text-navy-700 font-sans">Variable rate (SOFR-based or floating)</label>
              </div>

              {/* ── Amortization Section ────────────────── */}
              <div className="border-t border-ivory-300 pt-4 mt-2">
                <p className="text-xs font-sans font-semibold text-navy-500 uppercase tracking-wider mb-3">Amortization & Payments</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="input-label">Amortization Type</label>
                    <select className="input" value={form.amortizationType} onChange={(e) => setForm({ ...form, amortizationType: e.target.value })}>
                      <option value="">Select type</option>
                      {AMORT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="input-label">Payment Frequency</label>
                    <select className="input" value={form.paymentFrequency} onChange={(e) => setForm({ ...form, paymentFrequency: e.target.value })}>
                      {PAYMENT_FREQUENCIES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="input-label">Annual Debt Service ($)</label>
                    <input type="number" className="input" value={form.annualDebtService} onChange={(e) => setForm({ ...form, annualDebtService: e.target.value })} placeholder="0" min="0" />
                  </div>
                  {(form.amortizationType === 'balloon' || form.amortizationType === 'amortizing') && (
                    <div>
                      <label className="input-label">Amortization Schedule (months)</label>
                      <input type="number" className="input" value={form.amortizationTermMonths} onChange={(e) => setForm({ ...form, amortizationTermMonths: e.target.value })} placeholder="360 = 30yr" min="1" max="600" />
                      <p className="text-xs text-navy-400 mt-1 font-sans">If longer than remaining term, loan has a balloon</p>
                    </div>
                  )}
                </div>
                {form.amortizationType === 'balloon' && (
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="input-label">Balloon Override ($)</label>
                      <input type="number" className="input" value={form.balloonAmountOverride} onChange={(e) => setForm({ ...form, balloonAmountOverride: e.target.value })} placeholder="Auto-calculated if blank" min="0" />
                      <p className="text-xs text-navy-400 mt-1 font-sans">Leave blank to auto-calculate from am schedule</p>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Refinance Assumptions ───────────────── */}
              {(form.amortizationType === 'balloon' || form.amortizationType === 'io') && (
                <div className="border-t border-ivory-300 pt-4 mt-2">
                  <p className="text-xs font-sans font-semibold text-navy-500 uppercase tracking-wider mb-3">Refinance Assumptions</p>
                  <div className="flex items-center gap-3 mb-4">
                    <input type="checkbox" id="refiExpected" checked={form.refiExpected} onChange={(e) => setForm({ ...form, refiExpected: e.target.checked })} className="w-4 h-4 rounded border-ivory-400 text-navy-600 focus:ring-navy-300" />
                    <label htmlFor="refiExpected" className="text-sm text-navy-700 font-sans">Expect to refinance at maturity</label>
                  </div>
                  {form.refiExpected && (
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="input-label">Expected Rate (%)</label>
                        <input type="number" className="input" value={form.refiRate} onChange={(e) => setForm({ ...form, refiRate: e.target.value })} placeholder="6.50" step="0.01" min="0" />
                      </div>
                      <div>
                        <label className="input-label">New Term (months)</label>
                        <input type="number" className="input" value={form.refiTermMonths} onChange={(e) => setForm({ ...form, refiTermMonths: e.target.value })} placeholder="120" min="1" max="600" />
                        <p className="text-xs text-navy-400 mt-1 font-sans">e.g. 60=5yr, 120=10yr</p>
                      </div>
                      <div>
                        <label className="input-label">New Am Schedule (months)</label>
                        <input type="number" className="input" value={form.refiAmTermMonths} onChange={(e) => setForm({ ...form, refiAmTermMonths: e.target.value })} placeholder="360" min="1" max="600" />
                        <p className="text-xs text-navy-400 mt-1 font-sans">Blank = fully amortizing</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Prepayment Section ──────────────────── */}
              <div className="border-t border-ivory-300 pt-4 mt-2">
                <p className="text-xs font-sans font-semibold text-navy-500 uppercase tracking-wider mb-3">Prepayment Terms</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="input-label">Prepayment Penalty (%)</label>
                    <input type="number" className="input" value={form.prepaymentPenaltyPct} onChange={(e) => setForm({ ...form, prepaymentPenaltyPct: e.target.value })} placeholder="0" step="0.1" min="0" />
                  </div>
                  <div>
                    <label className="input-label">Penalty Expires</label>
                    <input type="date" className="input" value={form.prepaymentPenaltyExpires} onChange={(e) => setForm({ ...form, prepaymentPenaltyExpires: e.target.value })} />
                  </div>
                </div>
              </div>

              <div>
                <label className="input-label">Notes</label>
                <textarea className="input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Covenants, collateral, special terms..." />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-ivory-300 bg-ivory-100 rounded-b-lg">
              <button onClick={() => setShowModal(false)} className="btn-secondary" disabled={saving}>Cancel</button>
              <button onClick={handleSave} className="btn-primary" disabled={saving}>{saving ? 'Saving...' : editingId ? 'Update' : 'Create Liability'}</button>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-xl border border-ivory-300 w-full max-w-sm mx-4 p-6">
            <h3 className="font-display text-lg text-navy-900 mb-2">Delete Liability</h3>
            <p className="text-sm text-navy-500 font-sans mb-6">Are you sure? This cannot be undone.</p>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setDeleteId(null)} className="btn-secondary" disabled={deleting}>Cancel</button>
              <button onClick={handleDelete} className="btn-burgundy" disabled={deleting}>{deleting ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
